import React, { useContext, useEffect } from "react";
import { Box, Typography, Tab, Grid, Stack } from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { AppContext } from "../context";
import { pathJoinUrl } from "@smfa/utils/src";
import { useAdminReducer } from "../hooks/useAdminReducer";
import {
  getMembershipBanner,
  getMembershipBenifit,
  updateMembershipBanner,
  updateMembershipBenifit,
} from "../actions/membership.actions";

// Common components
import LimitedTextField from "../components/common/LimitedTextField";
import FileUploadArea from "../components/common/FileUploadArea";
import FormActionBar from "../components/common/FormActionBar";
import ToolBody from "../components/common/ToolBody";

const BenefitsMembership = () => {
  const { storage } = useContext(AppContext);

  // Use the admin reducer hook for state management
  const {
    state,
    setLoading,
    isLoading,
    handleFieldChange,
    handleFormError,
    handleImageChange,
    setFormData,
    setSectionData,
    setMessage,
    setActiveTab,
    dispatch,
  } = useAdminReducer({
    // Banner form state
    sectionData: {
      membership_banner_section_id: 0,
      banner_title: "",
      banner_title_error: false,
      banner_title_maxlenth: 30,
    },
    // Benefits form state
    formData: {
      benefit_details_secion_id: 0,
      benefit_title: "",
      benefit_title_error: false,
      benefit_title_maxlenth: 30,
      benefit_short_desc: "",
      benefit_short_desc_error: false,
      benefit_short_desc_maxlenth: 380,
      benefit_details_one: "",
      benefit_details_one_error: false,
      benefit_details_one_maxlenth: 160,
      benefit_details_second: "",
      benefit_details_second_error: false,
      benefit_details_second_maxlenth: 140,
      benefit_details_third: "",
      benefit_details_third_error: false,
      benefit_details_third_maxlenth: 140,
      benefit_details_forth: "",
      benefit_details_forth_error: false,
      benefit_details_forth_maxlenth: 140,
      benefit_details_fifth: "",
      benefit_details_fifth_error: false,
      benefit_details_fifth_maxlenth: 140,
      benefit_details_six: "",
      benefit_details_six_error: false,
      benefit_details_six_maxlenth: 140,
      image_name: "",
      image_error: false,
      image_mob_name: "",
      image_mob_error: false,
    },
    // Image previews
    images: {
      deskImage: "",
      mobImage: "",
      benefitImage: "",
    },
    // Active tab state
    ui: {
      activeTab: "1",
    },
  });

  const { sectionData, formData, images } = state;
  const { successMsg, errorMsg } = state;
  const { deskImage, mobImage, benefitImage } = images;

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setMessage("", "");
    setLoading(false);

    // Try this alternative approach if setActiveTab isn't working
    dispatch({
      type: "SET_UI_STATE",
      ui: { activeTab: newValue },
    });

    // Also keep the original call just to be safe
    setActiveTab(newValue);
  };

  // Handle image changes
  const handleBannerImageChange = (event) =>
    handleImageChange(event, "banner_image", "deskImage");

  const handleBannerMobileImageChange = (event) =>
    handleImageChange(event, "banner_image_mob", "mobImage");

  const handleBenefitImageChange = (event) =>
    handleImageChange(event, "image", "benefitImage");

  const clearUpload = (imageField, previewField) => {
    dispatch({
      type: "SET_IMAGE",
      formName: "formData",
      imageField,
      file: null,
      fileName: "",
      previewField,
      previewUrl: "",
    });
  };

  // Submit banner form
  const submitBanner = () => {
    setLoading(true);
    setMessage("", "");

    // Form validation
    let hasError = false;
    if (!sectionData.banner_title) {
      handleFormError("banner_title", true, "sectionData");
      hasError = true;
    }

    if (hasError) {
      setLoading(false);
      return;
    }

    // Create form data
    const formData = new FormData();
    formData.append(
      "membership_banner_section_id",
      sectionData.membership_banner_section_id,
    );
    formData.append("banner_image_mob", state.formData.banner_image_mob || "");
    formData.append("banner_image", state.formData.banner_image || "");
    formData.append("banner_title", sectionData.banner_title);

    // Submit to API
    updateMembershipBanner(formData)
      .then((res) => {
        if (res.data.status === true) {
          setMessage("Banner Updated Successfully!", "");
          setTimeout(() => setMessage("", ""), 3000);
        } else {
          setMessage(
            "",
            res.data.message || "Something went wrong, please try again!",
          );
        }
      })
      .catch(() => {
        setMessage("", "Something went wrong, please try again!");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Submit benefits form
  const submitBenefits = () => {
    setLoading(true);
    setMessage("", "");

    // Form validation
    const requiredFields = [
      "benefit_title",
      "benefit_short_desc",
      "benefit_details_one",
      "benefit_details_second",
      "benefit_details_third",
      "benefit_details_forth",
      "benefit_details_fifth",
      "benefit_details_six",
    ];

    let hasError = false;
    requiredFields.forEach((field) => {
      if (!formData[field]) {
        handleFormError(field, true);
        hasError = true;
      }
    });

    if (hasError) {
      setLoading(false);
      return;
    }

    // Create form data
    const benefitFormData = new FormData();
    benefitFormData.append(
      "benefit_details_secion_id",
      formData.benefit_details_secion_id,
    );
    benefitFormData.append("image_mob", formData.image_mob || "");
    benefitFormData.append("image", formData.image || "");
    benefitFormData.append("benefit_title", formData.benefit_title);
    benefitFormData.append("benefit_short_desc", formData.benefit_short_desc);
    benefitFormData.append("benefit_details_one", formData.benefit_details_one);
    benefitFormData.append(
      "benefit_details_second",
      formData.benefit_details_second,
    );
    benefitFormData.append(
      "benefit_details_third",
      formData.benefit_details_third,
    );
    benefitFormData.append(
      "benefit_details_forth",
      formData.benefit_details_forth,
    );
    benefitFormData.append(
      "benefit_details_fifth",
      formData.benefit_details_fifth,
    );
    benefitFormData.append("benefit_details_six", formData.benefit_details_six);

    // Submit to API
    updateMembershipBenifit(benefitFormData)
      .then((res) => {
        if (res.data.status === true) {
          setMessage("Benefits Updated Successfully!", "");
          setTimeout(() => setMessage("", ""), 3000);
        } else {
          setMessage(
            "",
            res.data.message || "Something went wrong, please try again!",
          );
        }
      })
      .catch(() => {
        setMessage("", "Something went wrong, please try again!");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Load initial data on component mount
  useEffect(() => {
    // Get banner data
    getMembershipBanner().then((res) => {
      if (res.data.status === true) {
        // Set banner section data
        setSectionData({
          membership_banner_section_id:
            res.data.data.membership_banner_section_id || 0,
          banner_title: res.data.data.banner_title || "",
        });

        // Set banner images if they exist
        if (res.data.data.banner_image) {
          handleImageChange(
            { target: { files: null } },
            "banner_image",
            "deskImage",
            res.data.data.banner_image,
            pathJoinUrl(storage[0].imageEndUrl, res.data.data.banner_image),
          );
        }

        if (res.data.data.banner_image_mob) {
          handleImageChange(
            { target: { files: null } },
            "banner_image_mob",
            "mobImage",
            res.data.data.banner_image_mob,
            pathJoinUrl(storage[0].imageEndUrl, res.data.data.banner_image_mob),
          );
        }
      }
    });

    // Get benefits data
    getMembershipBenifit().then((res) => {
      if (res.data.status === true) {
        // Set benefits data
        setFormData({
          benefit_details_secion_id:
            res.data.data.benefit_details_secion_id || 0,
          benefit_title: res.data.data.benefit_title || "",
          benefit_short_desc: res.data.data.benefit_short_desc || "",
          benefit_details_one: res.data.data.benefit_details_one || "",
          benefit_details_second: res.data.data.benefit_details_second || "",
          benefit_details_third: res.data.data.benefit_details_third || "",
          benefit_details_forth: res.data.data.benefit_details_forth || "",
          benefit_details_fifth: res.data.data.benefit_details_fifth || "",
          benefit_details_six: res.data.data.benefit_details_six || "",
        });

        // Set benefit image if it exists
        if (res.data.data.image) {
          handleImageChange(
            { target: { files: null } },
            "image",
            "benefitImage",
            res.data.data.image,
            pathJoinUrl(storage[0].imageEndUrl, res.data.data.image),
          );
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ToolBody>
      <TabContext value={state.ui?.activeTab || "1"}>
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <TabList onChange={handleTabChange} aria-label="membership tabs">
            <Tab label="Banner" value="1" />
            <Tab label="Benefits of membership" value="2" />
          </TabList>
        </Box>

        {/* Tab 1: Banner */}
        <TabPanel value="1">
          <Stack spacing={4}>
            {/* Banner Title */}
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <LimitedTextField
                  id="banner_title"
                  name="banner_title"
                  label="Banner Title"
                  value={sectionData.banner_title}
                  onChange={(e) => handleFieldChange(e, "sectionData")}
                  maxLength={sectionData.banner_title_maxlenth}
                  error={sectionData.banner_title_error}
                  errorText="Please enter Title!"
                />
              </Grid>
            </Grid>

            {/* Banner Images */}
            <Grid container spacing={4}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FileUploadArea
                  title="Add Banner Image For Desktop"
                  description={
                    <>
                      Best image Resolution: 1920px X 836px
                      <br />
                      Supported files types: JPG, JPEG & PNG
                    </>
                  }
                  previewUrl={deskImage}
                  fileName={formData.banner_image_name}
                  onChange={handleBannerImageChange}
                  deleteRequest={{
                    resource: "membership-banner",
                    id: sectionData.membership_banner_section_id,
                    field: "banner_image",
                  }}
                  onRemove={() => clearUpload("banner_image", "deskImage")}
                  accept=".png,.jpg,.jpeg"
                  error={formData.banner_image_error}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <FileUploadArea
                  title="Add Banner Image For Mobile"
                  description={
                    <>
                      Best image Resolution: 400px X 350px
                      <br />
                      Supported files types: JPG, JPEG & PNG
                    </>
                  }
                  previewUrl={mobImage}
                  fileName={formData.banner_image_mob_name}
                  onChange={handleBannerMobileImageChange}
                  deleteRequest={{
                    resource: "membership-banner",
                    id: sectionData.membership_banner_section_id,
                    field: "banner_image_mob",
                  }}
                  onRemove={() =>
                    clearUpload("banner_image_mob", "mobImage")
                  }
                  accept=".png,.jpg,.jpeg"
                  error={formData.banner_image_mob_error}
                />
              </Grid>
            </Grid>

            {/* Form Actions */}
            <FormActionBar
              onSubmit={submitBanner}
              isSubmitting={isLoading}
              isEdit={sectionData.membership_banner_section_id !== 0}
              successMessage={successMsg}
              errorMessage={errorMsg}
              submitLabel="Update"
            />
          </Stack>
        </TabPanel>

        {/* Tab 2: Benefits */}
        <TabPanel value="2">
          <Stack spacing={4}>
            {/* Heading & Description */}
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <LimitedTextField
                  id="benefit_title"
                  name="benefit_title"
                  label="Heading"
                  value={formData.benefit_title}
                  onChange={(e) => handleFieldChange(e)}
                  maxLength={formData.benefit_title_maxlenth}
                  error={formData.benefit_title_error}
                  errorText="Please enter Heading!"
                />
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <LimitedTextField
                  id="benefit_short_desc"
                  name="benefit_short_desc"
                  label="Short Description"
                  value={formData.benefit_short_desc}
                  onChange={(e) => handleFieldChange(e)}
                  maxLength={formData.benefit_short_desc_maxlenth}
                  error={formData.benefit_short_desc_error}
                  errorText="Please enter Short Description!"
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>

            {/* Details Section Title */}
            <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
              Details of Benefits
            </Typography>

            {/* Benefit Details */}
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <LimitedTextField
                  id="benefit_details_one"
                  name="benefit_details_one"
                  label="Details One"
                  value={formData.benefit_details_one}
                  onChange={(e) => handleFieldChange(e)}
                  maxLength={formData.benefit_details_one_maxlenth}
                  error={formData.benefit_details_one_error}
                  errorText="Please enter Details One!"
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <LimitedTextField
                  id="benefit_details_second"
                  name="benefit_details_second"
                  label="Details Two"
                  value={formData.benefit_details_second}
                  onChange={(e) => handleFieldChange(e)}
                  maxLength={formData.benefit_details_second_maxlenth}
                  error={formData.benefit_details_second_error}
                  errorText="Please enter Details Two!"
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <LimitedTextField
                  id="benefit_details_third"
                  name="benefit_details_third"
                  label="Details Three"
                  value={formData.benefit_details_third}
                  onChange={(e) => handleFieldChange(e)}
                  maxLength={formData.benefit_details_third_maxlenth}
                  error={formData.benefit_details_third_error}
                  errorText="Please enter Details Three!"
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <LimitedTextField
                  id="benefit_details_forth"
                  name="benefit_details_forth"
                  label="Details Four"
                  value={formData.benefit_details_forth}
                  onChange={(e) => handleFieldChange(e)}
                  maxLength={formData.benefit_details_forth_maxlenth}
                  error={formData.benefit_details_forth_error}
                  errorText="Please enter Details Four!"
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <LimitedTextField
                  id="benefit_details_fifth"
                  name="benefit_details_fifth"
                  label="Details Five"
                  value={formData.benefit_details_fifth}
                  onChange={(e) => handleFieldChange(e)}
                  maxLength={formData.benefit_details_fifth_maxlenth}
                  error={formData.benefit_details_fifth_error}
                  errorText="Please enter Details Five!"
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <LimitedTextField
                  id="benefit_details_six"
                  name="benefit_details_six"
                  label="Details Six"
                  value={formData.benefit_details_six}
                  onChange={(e) => handleFieldChange(e)}
                  maxLength={formData.benefit_details_six_maxlenth}
                  error={formData.benefit_details_six_error}
                  errorText="Please enter Details Six!"
                />
              </Grid>
            </Grid>

            {/* Benefit Image */}
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FileUploadArea
                  title="Upload Image"
                  description={
                    <>
                      Best image Resolution: 526px X 350px
                      <br />
                      Supported files types: JPG, JPEG & PNG
                    </>
                  }
                  previewUrl={benefitImage}
                  fileName={formData.image_name}
                  onChange={handleBenefitImageChange}
                  deleteRequest={{
                    resource: "membership-benefit",
                    id: formData.benefit_details_secion_id,
                    field: "image",
                  }}
                  onRemove={() => clearUpload("image", "benefitImage")}
                  accept=".png,.jpg,.jpeg"
                  error={formData.image_error}
                />
              </Grid>
            </Grid>

            {/* Form Actions */}
            <FormActionBar
              onSubmit={submitBenefits}
              isSubmitting={isLoading}
              isEdit={formData.benefit_details_secion_id !== 0}
              successMessage={successMsg}
              errorMessage={errorMsg}
              submitLabel="Update"
            />
          </Stack>
        </TabPanel>
      </TabContext>
    </ToolBody>
  );
};

export default BenefitsMembership;
